import type { ComponentType } from 'react';
import type { GetCurrentPlanQuery } from '@frameio/graphql-client/generated';
import {
  ArchivedStorageIcon,
  MountedStorageIcon,
  StandardStorageIcon,
} from '@frameio/shared/icons';
import type { TrackingEventName } from '@frameio/shared/segment/hooks/types';
import {
  ARCHIVED_STORAGE_LIMIT_MODIFIER,
  MOUNTED_STORAGE_LIMIT_MODIFIER,
  STORAGE_LIMIT_MODIFIER,
} from '../../modal/components/ConversionDialog/constants';
import type { StorageVariant } from '../../modal/components/ConversionDialog/types';
import TEXT from './generated/TEXT/storageVariantConfig';

TEXT.DEFINE({
  /** Title for standard storage total display */
  totalActiveStorage: 'Total Standard Storage',
  /** Title for archival storage total display */
  totalArchivalStorage: 'Total Archival Storage',
  /** Title for mounted storage total display */
  totalMountedStorage: 'Total Mounted Storage',
  /** Dialog title for managing standard storage */
  manageActiveStorage: 'Manage Standard Storage',
  /** Dialog title for managing archival storage */
  manageArchivalStorage: 'Manage Archival Storage',
  /** Dialog title for managing mounted storage */
  manageMountedStorage: 'Manage Mounted Storage',
  /** Storage type label used in add-on action descriptions for active storage */
  activeStorageType: 'storage',
  /** Storage type label used in add-on action descriptions for archival storage */
  archivalStorageType: 'archival storage',
  /** Storage type label used in add-on action descriptions for mounted storage */
  mountedStorageType: 'mounted storage',
});

type Subscription = NonNullable<GetCurrentPlanQuery['account']>['subscription'];

type StorageVariantConfig = {
  icon: ComponentType<{ color: string }>;
  totalStorageLabel: string;
  manageStorageLabel: string;
  addOnModifier: string;
  storageTypeLabel: string;
  startedEvent: TrackingEventName;
  quantitySetEvent: TrackingEventName;
  getTotalStorage: (subscription: Subscription | undefined) => number;
  getIncludedStorage: (subscription: Subscription | undefined) => number;
  getPerUserStorage: (subscription: Subscription | undefined) => number;
};

export const STORAGE_VARIANT_CONFIG: Record<
  StorageVariant,
  StorageVariantConfig
> = {
  active: {
    icon: StandardStorageIcon,
    get totalStorageLabel() {
      return TEXT.totalActiveStorage;
    },
    get manageStorageLabel() {
      return TEXT.manageActiveStorage;
    },
    addOnModifier: STORAGE_LIMIT_MODIFIER,
    get storageTypeLabel() {
      return TEXT.activeStorageType;
    },
    startedEvent: 'storage-add-on-started',
    quantitySetEvent: 'storage-add-on-quantity-set',
    getTotalStorage: (sub) => sub?.limitedResources?.storageBytes?.limit53 ?? 0,
    getIncludedStorage: (sub) => sub?.plan?.activeStorageLimit ?? 0,
    getPerUserStorage: (sub) => sub?.perUserStorage?.limit ?? 0,
  },
  archival: {
    icon: ArchivedStorageIcon,
    get totalStorageLabel() {
      return TEXT.totalArchivalStorage;
    },
    get manageStorageLabel() {
      return TEXT.manageArchivalStorage;
    },
    addOnModifier: ARCHIVED_STORAGE_LIMIT_MODIFIER,
    get storageTypeLabel() {
      return TEXT.archivalStorageType;
    },
    startedEvent: 'storage-add-on-started',
    quantitySetEvent: 'storage-add-on-quantity-set',
    getTotalStorage: (sub) =>
      sub?.limitedResources?.archivedStorageBytes?.limit53 ?? 0,
    getIncludedStorage: (sub) => sub?.plan?.archivedStorageLimit ?? 0,
    getPerUserStorage: () => 0,
  },
  mounted: {
    icon: MountedStorageIcon,
    get totalStorageLabel() {
      return TEXT.totalMountedStorage;
    },
    get manageStorageLabel() {
      return TEXT.manageMountedStorage;
    },
    addOnModifier: MOUNTED_STORAGE_LIMIT_MODIFIER,
    get storageTypeLabel() {
      return TEXT.mountedStorageType;
    },
    startedEvent: 'mounted-storage-add-on-started',
    quantitySetEvent: 'mounted-storage-add-on-quantity-set',
    getTotalStorage: (sub) =>
      sub?.limitedResources?.mountedStorageBytes?.limit53 ?? 0,
    getIncludedStorage: (sub) => sub?.plan?.mountedStorageLimit ?? 0,
    getPerUserStorage: () => 0,
  },
};
